// Client-side ICS (iCalendar) generation.
//
// The interviewer "Download .ics" button builds the calendar file on the fly in
// the browser from data already loaded on the page — nothing is stored on the
// server. Because it is rebuilt from the booking record every time, the button
// can be used at any point while the session is upcoming.
//
// Timestamps are emitted in UTC (the trailing "Z"), which is unambiguous and
// inherently DST-safe — the local timezone is shown as a label inside the event
// text for the reader's convenience.

import { tzAbbr } from './timezone'

/** Format a UTC ISO string as an iCalendar UTC timestamp: YYYYMMDDTHHMMSSZ. */
function toIcsUtc(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}` +
    `T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`
  )
}

/** Escape a value for an iCalendar text field (RFC 5545 §3.3.11). */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

/**
 * Fold a content line to 75 octets per RFC 5545 §3.1. Continuation lines start
 * with a single space. Folding on characters (not bytes) is a safe approximation
 * for the mostly-ASCII content we emit here.
 */
function foldLine(line: string): string {
  if (line.length <= 75) return line
  const parts: string[] = []
  let rest = line
  parts.push(rest.slice(0, 75))
  rest = rest.slice(75)
  while (rest.length > 74) {
    parts.push(' ' + rest.slice(0, 74))
    rest = rest.slice(74)
  }
  if (rest.length) parts.push(' ' + rest)
  return parts.join('\r\n')
}

export interface InterviewerIcsInput {
  bookingId: number
  clientName: string | null
  clientId: number | null
  title: string | null
  targetRole: string | null
  /** UTC ISO start time. */
  scheduledStartUtc: string
  durationMinutes: number
  /** Preferred: the host/start URL; falls back to the join URL. */
  zoomUrl: string | null
  zoomPassword: string | null
  /** IANA timezone, used only to label the time inside the event text. */
  timezone?: string
  /**
   * Stable session UID root (`idr-session-{root}`) spanning the reschedule
   * chain — when present, a re-downloaded .ics UPDATES the original calendar
   * event instead of duplicating. Absent → falls back to the per-booking UID.
   */
  calendarUid?: string | null
  /** Monotonic reschedule counter emitted as the VEVENT SEQUENCE. */
  calendarSequence?: number
}

/**
 * Build an interviewer-facing iCalendar (VEVENT) string.
 *
 * Content follows the interviewer ICS spec: session title, date/time, timezone
 * label, Zoom launch link, client name, role/interview type, a link to the
 * client profile/intake inside the platform, and reminders to review materials
 * and to follow the Zoom spotlight/recording workflow.
 */
export function buildInterviewerIcs(input: InterviewerIcsInput): string {
  const start = toIcsUtc(input.scheduledStartUtc)
  const endMs = new Date(input.scheduledStartUtc).getTime() + input.durationMinutes * 60000
  const end = toIcsUtc(new Date(endMs).toISOString())
  const stamp = toIcsUtc(new Date().toISOString())

  const client = input.clientName?.trim() || 'Client'
  const role = input.targetRole?.trim()
  const summary =
    (input.title?.trim() || `Mock Interview with ${client}`) + ' — Interview Dress Rehearsal (Interviewer)'

  const tzLabel = input.timezone ? ` (${tzAbbr(input.timezone)} — ${input.timezone})` : ''

  // Deep link to the client's intake/profile inside the platform.
  const profileUrl =
    input.clientId != null
      ? `${window.location.origin}/interviewer/clients?client=${input.clientId}&booking=${input.bookingId}`
      : `${window.location.origin}/interviewer/home`

  const descLines = [
    `Candidate: ${client}`,
    role ? `Role / interview type: ${role}` : null,
    `Session time shown in your timezone${tzLabel}.`,
    '',
    input.zoomUrl ? `Zoom launch link (start as host): ${input.zoomUrl}` : 'Zoom link: check the platform.',
    input.zoomPassword ? `Meeting password: ${input.zoomPassword}` : null,
    '',
    `Client profile / intake: ${profileUrl}`,
    '',
    'Reminder: review the client materials and prepared questions before the session.',
    'Reminder: follow the required Zoom spotlight / recording workflow once the session starts.',
  ].filter((l): l is string => l !== null)
  const description = descLines.join('\n')

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Interview Dress Rehearsal//Interviewer//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${
      input.calendarUid
        ? `${input.calendarUid}-interviewer@interviewdressrehearsal.com`
        : `idr-booking-${input.bookingId}-interviewer@interviewdressrehearsal.com`
    }`,
    `SEQUENCE:${input.calendarSequence ?? 0}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeText(summary)}`,
    `DESCRIPTION:${escapeText(description)}`,
    `LOCATION:${escapeText(input.zoomUrl ?? 'Zoom Meeting')}`,
    'BEGIN:VALARM',
    'TRIGGER:-PT30M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: Interview in 30 minutes — review client materials',
    'END:VALARM',
    'BEGIN:VALARM',
    'TRIGGER:-PT10M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: Interview in 10 minutes',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ]

  return lines.map(foldLine).join('\r\n')
}

// ── Shared VEVENT assembly ──────────────────────────────────────────────────

interface VeventInput {
  uid: string
  /** VEVENT SEQUENCE — bump so re-imports UPDATE the event of the same UID. */
  sequence: number
  scheduledStartUtc: string
  durationMinutes: number
  summary: string
  description: string
  location: string
  prodid: string
}

function buildVcalendar(v: VeventInput): string {
  const start = toIcsUtc(v.scheduledStartUtc)
  const endMs = new Date(v.scheduledStartUtc).getTime() + v.durationMinutes * 60000
  const end = toIcsUtc(new Date(endMs).toISOString())
  const stamp = toIcsUtc(new Date().toISOString())

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:${v.prodid}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${v.uid}`,
    `SEQUENCE:${v.sequence}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeText(v.summary)}`,
    `DESCRIPTION:${escapeText(v.description)}`,
    `LOCATION:${escapeText(v.location)}`,
    'BEGIN:VALARM',
    'TRIGGER:-PT30M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: Interview in 30 minutes',
    'END:VALARM',
    'BEGIN:VALARM',
    'TRIGGER:-PT10M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: Interview in 10 minutes',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
  return lines.map(foldLine).join('\r\n')
}

export interface ClientIcsInput {
  bookingId: number
  /** Interviewer DISPLAY name/alias only — never personal identity. */
  interviewerAlias: string | null
  title: string | null
  scheduledStartUtc: string
  durationMinutes: number
  /** Client JOIN link only — never the interviewer host/start URL. */
  zoomJoinUrl: string | null
  zoomPassword: string | null
  /** IANA timezone, used only to label the time inside the event text. */
  timezone?: string
  /** Stable session UID root (`idr-session-{root}`); absent → per-booking UID. */
  calendarUid?: string | null
  /** Monotonic reschedule counter emitted as the VEVENT SEQUENCE. */
  calendarSequence?: number
}

/**
 * Build a client-facing iCalendar string.
 *
 * Privacy: exposes only the interviewer's DISPLAY alias and the client JOIN
 * link — never the interviewer's personal email, calendar, host/start URL, or
 * other identifying info (Custom Calendar privacy spec / point 14).
 */
export function buildClientIcs(input: ClientIcsInput): string {
  const coach = input.interviewerAlias?.trim()
  const summary =
    (input.title?.trim() || 'Mock Interview') +
    (coach ? ` with ${coach}` : '') +
    ' — Interview Dress Rehearsal'
  const tzLabel = input.timezone ? ` (${tzAbbr(input.timezone)} — ${input.timezone})` : ''

  const descLines = [
    coach ? `Interview coach: ${coach}` : null,
    `Session time shown in your timezone${tzLabel}.`,
    '',
    input.zoomJoinUrl ? `Join the Zoom meeting: ${input.zoomJoinUrl}` : 'Zoom link: check the platform.',
    input.zoomPassword ? `Meeting password: ${input.zoomPassword}` : null,
    '',
    'Instructions: join a few minutes early from a quiet space with a stable connection, camera, and mic.',
    'Support: use the Help section inside the Interview Dress Rehearsal platform.',
    'Need to cancel or reschedule? Manage this session under "My Interviews" (subject to the cancellation window).',
  ].filter((l): l is string => l !== null)

  return buildVcalendar({
    uid: input.calendarUid
      ? `${input.calendarUid}@interviewdressrehearsal.com`
      : `idr-booking-${input.bookingId}@interviewdressrehearsal.com`,
    sequence: input.calendarSequence ?? 0,
    scheduledStartUtc: input.scheduledStartUtc,
    durationMinutes: input.durationMinutes,
    summary,
    description: descLines.join('\n'),
    location: input.zoomJoinUrl ?? 'Zoom Meeting',
    prodid: '-//Interview Dress Rehearsal//Client//EN',
  })
}

export interface AdminIcsInput {
  bookingId: number
  clientName: string | null
  /** Interviewer display alias. */
  interviewerAlias: string | null
  scheduledStartUtc: string
  durationMinutes: number
  status?: string | null
  /** The single Zoom meeting JOIN link — admin never receives the host token. */
  zoomJoinUrl: string | null
  zoomPassword: string | null
  /** IANA timezone (admin/platform), used only to label the time. */
  timezone?: string
  /** Stable session UID root (`idr-session-{root}`); absent → per-booking UID. */
  calendarUid?: string | null
  /** Monotonic reschedule counter emitted as the VEVENT SEQUENCE. */
  calendarSequence?: number
}

/**
 * Build an admin-facing iCalendar string covering BOTH parties (client name +
 * interviewer alias) with the single Zoom meeting join link. Excludes the
 * interviewer host/start token (Design Rule 6).
 */
export function buildAdminIcs(input: AdminIcsInput): string {
  const client = input.clientName?.trim() || 'Client'
  const coach = input.interviewerAlias?.trim() || 'Interviewer'
  const summary = `Interview: ${client} × ${coach} — Interview Dress Rehearsal (Admin)`
  const tzLabel = input.timezone ? ` (${tzAbbr(input.timezone)} — ${input.timezone})` : ''

  const descLines = [
    `Client: ${client}`,
    `Interviewer: ${coach}`,
    input.status ? `Status: ${input.status}` : null,
    `Session time shown in the platform timezone${tzLabel}.`,
    '',
    input.zoomJoinUrl ? `Zoom meeting link: ${input.zoomJoinUrl}` : 'Zoom link: not available yet.',
    input.zoomPassword ? `Meeting password: ${input.zoomPassword}` : null,
    '',
    'Admin oversight copy — manage this session from the Session Management console.',
  ].filter((l): l is string => l !== null)

  return buildVcalendar({
    uid: input.calendarUid
      ? `${input.calendarUid}-admin@interviewdressrehearsal.com`
      : `idr-booking-${input.bookingId}-admin@interviewdressrehearsal.com`,
    sequence: input.calendarSequence ?? 0,
    scheduledStartUtc: input.scheduledStartUtc,
    durationMinutes: input.durationMinutes,
    summary,
    description: descLines.join('\n'),
    location: input.zoomJoinUrl ?? 'Zoom Meeting',
    prodid: '-//Interview Dress Rehearsal//Admin//EN',
  })
}

/** Trigger a browser download of an .ics file. */
export function downloadIcs(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
